import moment from "moment";
import { EMPTY_TARGETING, PRICE_PER_DAY } from "../../adManagementFormConfig";
import { CONTENT_REDIRECT_TO_INT } from "../ContentRedirectOption/ContentRedirectOption";

/* BannerPosition enum: Top=1, Featured=2 */
export const POSITION_TO_INT = { Top: 1, Featured: 2 };
export const INT_TO_POSITION = { 1: "Top", 2: "Featured" };

/* Payment status enum returned by the Ad APIs.
 * Backend integer (paymentStatus) + display text (paymentStatusText). */
export const PAYMENT_STATUS = Object.freeze({
  DRAFT: 0,
  PURCHASED: 1,
  PENDING_PAYMENT: 2,
});
export const PAYMENT_STATUS_TEXT = Object.freeze({
  DRAFT: "Draft",
  PURCHASED: "Purchased",
  PENDING_PAYMENT: "Pending Payment",
});

/* GET /api/v1/Ad/{adId} `images[].deviceType` integer mapping. */
export const DEVICE_TYPE_MAP = { 1: "mobile", 2: "tablet", 3: "windows" };

/** Returns the first candidate value that is an absolute http(s) URL, else null. */
function pickHttpUrl(...candidates) {
  for (const candidate of candidates) {
    if (candidate?.startsWith("http")) return candidate;
  }
  return null;
}

/**
 * Maps internal targeting state (camelCase keys, option-object arrays) to the
 * PascalCase shape expected by the API's TargetingJson field.
 */
export function mapTargetingToApi(targeting) {
  const toIdArray = (val, key) => {
    if (Array.isArray(val)) {
      // For numberOfEmployees, use label instead of value
      if (key === "numberOfEmployees") {
        return val.map((item) =>
          typeof item === "object" ? item.label : item,
        );
      }
      return val.map((item) => (typeof item === "object" ? item.value : item));
    }
    if (typeof val === "string" && val.trim()) {
      // For numberOfEmployees, use string as-is
      if (key === "numberOfEmployees") {
        return [val.trim()];
      }
      return [Number.parseInt(val.trim(), 10)];
    }
    return [];
  };
  const result = {
    SubscriberTypeIds: toIdArray(targeting.subscriberType, "subscriberType"),
    CarrierRepresentIds: toIdArray(
      targeting.carriersRepresented,
      "carriersRepresented",
    ),
    AreaIds: toIdArray(targeting.majorAreas, "majorAreas"),
    NumberOfProducerIds: toIdArray(
      targeting.numberOfProducers,
      "numberOfProducers",
    ),
    IndustryGroupIds: toIdArray(targeting.industryGroups, "industryGroups"),
    TechnologyIds: toIdArray(
      targeting.technologyBeingUsed,
      "technologyBeingUsed",
    ),
    AnnualRevenueIds: toIdArray(targeting.annualRevenue, "annualRevenue"),
    NumberOfEmployeeIds: toIdArray(
      targeting.numberOfEmployees,
      "numberOfEmployees",
    ),
    CountryIds: toIdArray(targeting.country, "country"),
    StateIds: toIdArray(targeting.state, "state"),
    City: targeting.city ?? "",
    Zip: targeting.zip ?? "",
  };
  // Only add YearsInBusiness if not 0 or falsy
  const yib = Number(targeting.yearsInBusiness);
  if (yib) result.YearsInBusiness = yib;
  // Only add NumberOfPrincipals if not 0 or falsy
  const nop = Number(targeting?.numberOfPrincipals);
  if (nop) result.NumberOfPrincipals = nop;
  return result;
}

/** Resolves the ContentRedirectType integer from a form value (string key or raw int). */
function resolveRedirectType(raw) {
  if (raw === undefined || raw === null || raw === "") return 0;
  const mapped = CONTENT_REDIRECT_TO_INT[raw];
  if (mapped !== undefined) return mapped;
  return typeof raw === "number" ? raw : 0;
}

/** Appends every Targeting.* key (and the JSON mirror) to the FormData. */
function appendTargeting(formData, put, targetingApi) {
  Object.entries(targetingApi).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) =>
        formData.append(`Targeting.${key}`, String(item)),
      );
    } else {
      put(`Targeting.${key}`, value);
    }
  });
  put("TargetingJson", JSON.stringify(targetingApi));
}

/** Appends per-device banner files to the FormData when present. */
function appendDeviceFiles(formData, deviceFiles) {
  if (deviceFiles?.mobile) formData.append("MobileFile", deviceFiles.mobile);
  if (deviceFiles?.tablet) formData.append("TabletFile", deviceFiles.tablet);
  if (deviceFiles?.windows) formData.append("WindowsFile", deviceFiles.windows);
}

/** Appends ContentRedirect-related fields based on the resolved redirect type. */
function appendContentRedirect(formData, put, v, redirectType) {
  put("ContentRedirectType", redirectType || "");
  put("ExternalUrl", redirectType === 3 ? (v.externalUrl ?? "") : "");

  const intIds = (v.linkedContentIds ?? [])
    .map((id) => Number(id))
    .filter((n) => Number.isInteger(n) && n > 0);
  if (redirectType === 2) {
    intIds.forEach((id) => formData.append("LinkedContentIds", String(id)));
  }
  put("LinkedContentIdsJson", redirectType === 2 ? JSON.stringify(intIds) : "");
}

/** Appends Created/Updated audit fields. */
function appendAuditFields(put, currentUser, adId) {
  const userIdStr = currentUser?.userId ? String(currentUser.userId) : "";
  const auditName =
    `${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`.trim();
  if (!adId) {
    put("CreatedBy", userIdStr);
    put("CreatedByName", auditName);
  }
  put("UpdatedBy", userIdStr);
  put("UpdatedByName", auditName);
}

function resolveDiscountedAmount(value) {
  if (value === undefined || value === null) return null;
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  if (normalizedValue === "") return null;
  const amount = Number(normalizedValue);
  return Number.isFinite(amount) && amount > 0 ? normalizedValue : null;
}

/** Builds the FormData payload for POST /api/v1/Ad (add mode) and the
 *  PUT mirror. Matches the contract documented in the swagger / cURL:
 *  - Top-level flat fields (DistributorId, Title, BannerPosition, …)
 *  - Repeated `Targeting.<Key>` entries for every id-array, plus the
 *    serialised `TargetingJson` blob the API keeps for backwards-compat.
 *  - LinkedContentIds (repeated ints) + LinkedContentIdsJson for type 2.
 *  - ExternalUrl for type 3.
 *  - Per-device files (MobileFile / TabletFile / WindowsFile).
 *  - Audit fields (CreatedBy / UpdatedBy / *Name) sourced from currentUser.
 */
export function buildAdFormData({
  values,
  startDate,
  endDate,
  targeting,
  distributorId,
  deviceFiles,
  showToAll,
  currentUser,
  adId,
  status,
}) {
  const formData = new FormData();
  const v = values ?? {};

  /* Always-append helper: empty string if value is null/undefined, else stringified. */
  const put = (key, value) => {
    if (value === undefined || value === null) formData.append(key, "");
    else formData.append(key, String(value));
  };

  /* ── Core identifiers ── */
  if (adId) put("AdId", adId);
  put("DistributorId", distributorId ?? "");

  /* ── Basic info ── */
  put("Title", (v.bannerName ?? "").toString().trim());
  put("BannerPosition", v.position ? (POSITION_TO_INT[v.position] ?? "") : "");
  put("StartDate", startDate ? moment(startDate).format("YYYY-MM-DD") : "");
  put("EndDate", endDate ? moment(endDate).format("YYYY-MM-DD") : "");
  put("ShowToAllSubscribers", showToAll ? "true" : "false");
  put("Status", status ?? "");
  put("DiscountedAmount", resolveDiscountedAmount(v?.discountedAmount));

  /* ── Targeting / device files / content redirect / audit ── */
  appendTargeting(formData, put, mapTargetingToApi(targeting ?? {}));
  appendDeviceFiles(formData, deviceFiles);
  appendContentRedirect(
    formData,
    put,
    v,
    resolveRedirectType(v.ContentRedirectType),
  );
  appendAuditFields(put, currentUser, adId);


  // Add SelectedDeviceTypesJson: array of device type ints for selected devices
  // Use values.targetDevices (checkbox state) to track selected devices
  const DEVICE_TO_INT = { mobile: 1, tablet: 2, windows: 3 };
  const selectedDeviceTypes = Array.isArray(v.targetDevices)
    ? v.targetDevices.map((d) => DEVICE_TO_INT[d]).filter(Boolean)
    : [];
  put("SelectedDeviceTypesJson", JSON.stringify(selectedDeviceTypes));

  return formData;
}

/** Reads a file as a data URL and calls onLoad with the result. */
export function readFileAsDataUrl(file, onLoad) {
  const reader = new FileReader();
  reader.onloadend = () => onLoad(reader.result);
  reader.readAsDataURL(file);
}

/** Derives the selected target-device list from an existing API record. */
export function deriveTargetDevices(record) {
  if (!record) return [];
  /* Primary: images array returned by GET /api/v1/Ad/{adId} */
  if (Array.isArray(record.images) && record.images.length > 0) {
    return record.images
      .map((img) => DEVICE_TYPE_MAP[img.deviceType])
      .filter(Boolean);
  }
  /* Fallback: targetDevices string array (normalised to lowercase) */
  if (Array.isArray(record.targetDevices) && record.targetDevices.length > 0) {
    return record.targetDevices.map((d) => String(d).toLowerCase());
  }
  /* Legacy: individual file-URL fields */
  const hasMobile =
    record.mobileFile || record.mobileFileUrl || record.mobileGcpFileUrl;
  const hasTablet =
    record.tabletFile || record.tabletFileUrl || record.tabletGcpFileUrl;
  const hasWindows =
    record.windowsFile || record.windowsFileUrl || record.windowsGcpFileUrl;
  const devices = [];
  if (hasMobile) devices.push("mobile");
  if (hasTablet) devices.push("tablet");
  if (hasWindows) devices.push("windows");
  return devices;
}

/** Builds devicePreviewUrls map from API record's file URL fields. */
export function deriveDevicePreviewUrls(record) {
  if (!record) return {};
  const urls = {};
  /* Primary: images array returned by GET /api/v1/Ad/{adId} */
  if (Array.isArray(record.images)) {
    record.images.forEach((img) => {
      const device = DEVICE_TYPE_MAP[img.deviceType];
      if (!device) return;
      const url = pickHttpUrl(img.gcpFilePath, img.uniqueFileName);
      if (url) urls[device] = url;
    });
    if (Object.keys(urls).length > 0) return urls;
  }
  /* Legacy fallback: individual file-URL fields */
  const mobileUrl =
    record.mobileFile || record.mobileFileUrl || record.mobileGcpFileUrl;
  const tabletUrl =
    record.tabletFile || record.tabletFileUrl || record.tabletGcpFileUrl;
  const windowsUrl =
    record.windowsFile || record.windowsFileUrl || record.windowsGcpFileUrl;
  if (mobileUrl) urls.mobile = mobileUrl;
  if (tabletUrl) urls.tablet = tabletUrl;
  if (windowsUrl) urls.windows = windowsUrl;
  return urls;
}

/** Derives targeting state from API record (prefers `targeting` object, then `targetingJson`). */
export function deriveTargeting(record) {
  if (!record) return { ...EMPTY_TARGETING };

  let t = record.targeting;
  if (!t && record.targetingJson) {
    try {
      t = JSON.parse(record.targetingJson);
    } catch (_) {
      t = null;
    }
  }
  if (!t) return { ...EMPTY_TARGETING };

  // Helper to get first string value from array or fallback
  const firstStr = (arr) =>
    Array.isArray(arr) && arr.length ? String(arr[0]) : "";

  return {
    ...EMPTY_TARGETING,
    subscriberType: t.subscriberTypeIds ?? [],
    carriersRepresented: t.carrierRepresentIds ?? [],
    majorAreas: t.areaIds ?? [],
    numberOfProducers: firstStr(t.numberOfProducerIds),
    industryGroups: t.industryGroupIds ?? [],
    technologyBeingUsed: t.technologyIds ?? [],
    yearsInBusiness: t.yearsInBusiness ?? firstStr(t.yearsInBusinessIds) ?? "",
    annualRevenue: firstStr(t.annualRevenueIds),
    numberOfEmployees: firstStr(t.numberOfEmployeeIds),
    numberOfPrincipals:
      t.numberOfPrincipals ?? firstStr(t.numberOfPrincipalIds) ?? "",
    country: t.countryIds ?? [],
    state: t.stateIds ?? [],
    city: t.city ?? "",
    zip: t.zip ?? "",
  };
}

/** Computes the estimated cost for an ad based on schedule, position and targeting multiplier. */
export function calcEstimatedCost(
  startDate,
  endDate,
  position,
  targetingMultiplier,
) {
  if (!startDate || !endDate || !position) return 0;
  const days = Math.max(1, moment(endDate).diff(moment(startDate), "days") + 1);
  return days * (PRICE_PER_DAY[position] ?? 0) * targetingMultiplier;
}

/** Resolves the ad-form page title from the current mode flags. */
export function resolveAdPageTitle({ isViewMode, isEditMode }) {
  if (isViewMode) return "View Advertisement";
  if (isEditMode) return "Edit Advertisement";
  return "Create Advertisement";
}

/** Returns a Formik `setTouched` map that marks every top-level field touched. */
export function buildAllTouchedMap(values) {
  return Object.keys(values ?? {}).reduce(
    (acc, key) => ({ ...acc, [key]: true }),
    {},
  );
}

/** Counts targeting fields that currently hold a value (0 when show-to-all). */
export function countFilledTargetingFields(targeting, showToAll, fields) {
  if (showToAll) return 0;
  return fields.filter((f) => {
    const val = targeting?.[f.key];
    return Array.isArray(val) ? val.length > 0 : Boolean(val);
  }).length;
}

/** Resolves a File from either a direct File input or a change event. */
export function pickUploadedFile(fileOrEvent) {
  if (fileOrEvent instanceof File) return fileOrEvent;
  return fileOrEvent?.target?.files?.[0] ?? null;
}

/** Frontend device key → backend numeric code. */
const DEVICE_TO_INT = { mobile: 1, tablet: 2, windows: 3 };

/** Payload for POST /api/v1/Ad/cost/calculate. */
export function buildCostCalculatePayload({
  distributorId,
  position,
  startDate,
  endDate,
  targetDevices,
  showToAll,
  targeting,
  filledTargetingFieldCount,
}) {
  const devices =
    Array.isArray(targetDevices) && targetDevices.length > 0
      ? targetDevices
      : ["mobile"];
  return {
    DistributorId: Number(distributorId),
    BannerPosition: POSITION_TO_INT[position] ?? 1,
    StartDate: moment(startDate).format("YYYY-MM-DD"),
    EndDate: moment(endDate).format("YYYY-MM-DD"),
    TargetDevices: devices.map((d) => DEVICE_TO_INT[d] ?? 1),
    ShowToAllSubscribers: showToAll,
    Targeting: mapTargetingToApi(targeting ?? {}),
    FilledTargetingFieldCount: filledTargetingFieldCount,
  };
}

/** Expands availability ranges into individual moment days. */
export function expandUnavailableDateRanges(ranges) {
  const list = Array.isArray(ranges) ? ranges : [];
  const dates = [];
  list.forEach((range) => {
    const start = moment(range?.startDate ?? range?.StartDate);
    const end = moment(range?.endDate ?? range?.EndDate);
    if (!start.isValid() || !end.isValid()) return;
    const cursor = start.clone().startOf("day");
    const last = end.clone().startOf("day");
    while (cursor.isSameOrBefore(last, "day")) {
      dates.push(cursor.clone());
      cursor.add(1, "day");
    }
  });
  return dates;
}

/** Purchased / locked flags derived from mode + record. */
export function derivePurchaseFlags({ isEditMode, isPurchased, isSuperAdmin }) {
  const isLimited = isEditMode && isPurchased;
  return {
    isLimited,
    isSuperAdminEdit: isSuperAdmin && isEditMode,
    isFieldLocked: isLimited,
  };
}

/** Locks discounted draft ads for non-admin users after an override price is applied. */
export function deriveOverridePriceDraftLock({
  isEditMode,
  paymentStatus,
  paymentStatusText,
  discountedAmount,
  isAdminOrSuperAdmin,
}) {
  const amount = Number(discountedAmount);
  const hasOverridePrice = Number.isFinite(amount) && amount > 0;
  const paymentStatusNumber = Number(paymentStatus);
  const normalizedPaymentStatusText = String(paymentStatusText ?? "")
    .trim()
    .toLowerCase();
  const isDraft =
    paymentStatusNumber === PAYMENT_STATUS.DRAFT ||
    normalizedPaymentStatusText === PAYMENT_STATUS_TEXT.DRAFT.toLowerCase();
  return Boolean(
    isEditMode && hasOverridePrice && isDraft && !isAdminOrSuperAdmin,
  );
}

/** Permission / button-enable flags derived from permissions + form state. */
export function derivePermissionFlags({
  permissions,
  isAddMode,
  isViewMode,
  formikIsValid,
  isLimited,
  onEdit,
  createPerm,
  updatePerm,
  isSuperAdmin,
  isPurchasedDistributorEdit,
}) {
  const canCreate = permissions.includes(createPerm);
  const canUpdate = permissions.includes(updatePerm);
  const hasWritePermission = isAddMode ? canCreate : canUpdate;
  return {
    canUpdate,
    hasWritePermission,
    canSave: formikIsValid && hasWritePermission,
    canPublish: Boolean(
      formikIsValid &&
      hasWritePermission &&
      (!isLimited || isPurchasedDistributorEdit || isSuperAdmin),
    ),
    editHandler: isViewMode && canUpdate ? onEdit : null,
  };
}
